import React, { useState, useCallback, useEffect } from 'react';
import { List, useTable, DeleteButton, FilterDropdown } from '@refinedev/antd';
import {
  Table,
  Space,
  Tag,
  Grid,
  Button,
  Input,
  Row,
  Col,
  message,
  Select,
  Card,
  Statistic,
  Tooltip,
  Typography,
  Segmented,
} from 'antd';
import { IResourceComponentsProps, BaseRecord, CrudFilters } from '@refinedev/core';
import {
  EditOutlined,
  EyeOutlined,
  DownloadOutlined,
  SearchOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { adminApi } from '@services/api';
import type { PlayerOwnershipStatus } from '@/types';

const PLAYER_TYPES = ['Batsman', 'Bowler', 'All-Rounder'];
const PLAYER_TYPE_COLORS = ['blue', 'red', 'purple'];

const TEAMS = [
  'AFGHANISTAN',
  'AUSTRALIA',
  'BANGLADESH',
  'ENGLAND',
  'INDIA',
  'NEW ZEALAND',
  'PAKISTAN',
  'SOUTH AFRICA',
  'SRI LANKA',
  'WEST INDIES',
];

const BOWLER_TYPES: Record<number, string> = {
  [-1]: 'Non-Bowler',
  0: 'Fast',
  1: 'Off Break',
  2: 'Leg Break',
  3: 'Medium',
};

const OWNERSHIP_HELP =
  'Distinct gamers per player, not cards. Dropped: own at least one card of this player now, from any source ' +
  '(starter and reward cards included, discarded cards not). In Roster: have him in any HOME or AWAY squad slot. ' +
  'In Playing XI: have him in one of the 11 playing positions. A card variant counts only cards of that variant; ' +
  'a gamer holding him in two variants counts under both, so the variants add up to more than All. ' +
  'Recounted from the database at most every 15 minutes.';

/** player_cards.rarity → variant name. The list's `rarity` filter switches the three counts to one variant. */
const CARD_VARIANTS = ['Rookie', 'Semi-Pro', 'Pro', 'Legendary'];
const ALL_VARIANTS = 'all';
type VariantChoice = number | typeof ALL_VARIANTS;

/** The variant in the table filters; from the URL it arrives as a string. */
const variantFromFilters = (filters: CrudFilters): VariantChoice => {
  const filter = filters.find((f) => 'field' in f && f.field === 'rarity');
  const raw: unknown = Array.isArray(filter?.value) ? filter?.value[0] : filter?.value;
  const rarity = raw === undefined || raw === null || raw === '' ? Number.NaN : Number(raw);
  return Number.isInteger(rarity) && rarity >= 0 && rarity < CARD_VARIANTS.length
    ? rarity
    : ALL_VARIANTS;
};

type CountField = 'dropped' | 'inRoster' | 'inPlayingXi';

/**
 * A row's count, or undefined when the row doesn't confirm the selected variant
 * (`countsRarity`): a server without variant support ignores the filter and
 * would otherwise show all-variant numbers under a variant's name.
 */
const countOf = (record: BaseRecord, field: CountField, variant: VariantChoice) =>
  variant === ALL_VARIANTS || record.countsRarity === variant
    ? (record[field] as number | undefined)
    : undefined;

const OWNERSHIP_POLL_MS = 5000;
/** Stop waiting for a recount after 3 minutes; the next page load picks it up. */
const OWNERSHIP_POLL_MAX = 36;

/** `computedAt` may arrive missing instead of null; treat both as "not counted yet". */
const normaliseStatus = (status: Partial<PlayerOwnershipStatus>): PlayerOwnershipStatus => ({
  computedAt: status.computedAt ?? null,
  refreshing: Boolean(status.refreshing),
});

const fmtCount = (value?: number | null) =>
  value === undefined || value === null ? '—' : value.toLocaleString('en-IN');

const shareOf = (part?: number, whole?: number) =>
  part === undefined || whole === undefined || whole === 0
    ? null
    : `${((part / whole) * 100).toFixed(1)}%`;

const formatIst = (iso: string) =>
  `${new Date(iso).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })} IST`;

interface RosterStats {
  totalPlayers: number;
  activePlayers: number;
  maxPlayerIndex: number;
  byRole: {
    batters: number;
    bowlers: number;
    allRounders: number;
    wicketKeepers: number;
  };
  winnerAlliance: number;
}

export const PlayerDataList: React.FC<IResourceComponentsProps> = () => {
  const screens = Grid.useBreakpoint();
  const [exporting, setExporting] = useState(false);
  const [rosterStats, setRosterStats] = useState<RosterStats | null>(null);

  useEffect(() => {
    adminApi
      .getRosterSize()
      .then((res) => setRosterStats(res.data))
      .catch(() => {
        /* keep null — header just won't render */
      });
  }, []);

  const { tableProps, tableQueryResult, searchFormProps, filters, setFilters, setCurrent } =
    useTable({
      syncWithLocation: true,
      pagination: { pageSize: 20 },
      onSearch: (values: { search?: string }) => {
        return [{ field: 'search', operator: 'eq', value: values.search || undefined }];
      },
    });

  const variant = variantFromFilters(filters);
  // Checked only once the fetch settles: while a new variant loads, the table
  // still holds the previous variant's rows.
  const variantIgnored =
    variant !== ALL_VARIANTS &&
    !tableQueryResult?.isFetching &&
    (tableQueryResult?.data?.data ?? []).some(
      (r) => r.dropped !== undefined && r.countsRarity !== variant
    );
  const handleVariantChange = useCallback(
    (value: VariantChoice) => {
      // An undefined value drops the filter, which means all variants.
      setFilters([
        { field: 'rarity', operator: 'eq', value: value === ALL_VARIANTS ? undefined : value },
      ]);
      setCurrent(1);
    },
    [setFilters, setCurrent]
  );

  // Dropped / In Roster / In Playing XI come from a snapshot the backend
  // recounts in the background; this tracks when it was taken and waits for a
  // recount to land before reloading the table.
  const [ownership, setOwnership] = useState<PlayerOwnershipStatus | null>(null);
  const [waitFor, setWaitFor] = useState<{ since: string | null } | null>(null);
  const refetchTable = tableQueryResult?.refetch;

  useEffect(() => {
    adminApi
      .getPlayerOwnershipStatus()
      .then((res) => {
        const status = normaliseStatus(res.data);
        setOwnership(status);
        if (status.refreshing) setWaitFor({ since: status.computedAt });
      })
      .catch(() => {
        /* keep null — the line reads "not counted yet" */
      });
  }, []);

  useEffect(() => {
    if (!waitFor) return undefined;
    let attempts = 0;
    const timer = setInterval(() => {
      attempts += 1;
      adminApi
        .getPlayerOwnershipStatus()
        .then((res) => {
          const status = normaliseStatus(res.data);
          setOwnership(status);
          const landed = status.computedAt !== null && status.computedAt !== waitFor.since;
          if (landed) {
            setWaitFor(null);
            void refetchTable?.();
          } else if (attempts >= OWNERSHIP_POLL_MAX) {
            setWaitFor(null);
            message.warning(
              'The recount is taking longer than usual. Reload the page in a few minutes.'
            );
          }
        })
        .catch(() => setWaitFor(null));
    }, OWNERSHIP_POLL_MS);
    return () => clearInterval(timer);
  }, [waitFor, refetchTable]);

  const handleRefreshCounts = useCallback(async () => {
    try {
      const res = await adminApi.refreshPlayerOwnership();
      const status = normaliseStatus(res.data);
      if (!status.refreshing) {
        message.error('Could not start the recount');
        return;
      }
      message.info('Recounting — the numbers update in a minute or two');
      setWaitFor({ since: ownership?.computedAt ?? null });
    } catch {
      message.error('Could not start the recount');
    }
  }, [ownership]);

  const ownershipLabel = ownership?.computedAt
    ? `Dropped / In Roster / In Playing XI counted ${formatIst(ownership.computedAt)}`
    : waitFor || ownership?.refreshing
      ? 'Counting Dropped / In Roster / In Playing XI…'
      : 'Dropped / In Roster / In Playing XI not counted yet';

  const handleExport = useCallback(async (includeBaseStats: boolean) => {
    setExporting(true);
    try {
      const res = await adminApi.exportPlayerData(includeBaseStats);
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `player-data${includeBaseStats ? '-with-stats' : ''}-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      message.success('Downloaded');
    } catch {
      message.error('Export failed');
    }
    setExporting(false);
  }, []);

  return (
    <List
      headerButtons={({ defaultButtons }) => (
        <Space>
          {defaultButtons}
          <Button
            icon={<DownloadOutlined />}
            onClick={() => handleExport(false)}
            loading={exporting}
          >
            Export CSV
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={() => handleExport(true)}
            loading={exporting}
          >
            Export with Base Stats
          </Button>
        </Space>
      )}
    >
      {rosterStats && (
        <Row gutter={[8, 8]} style={{ marginBottom: 16 }}>
          <Col xs={12} sm={8} md={6} lg={3}>
            <Card size="small">
              <Statistic title="Total Players" value={rosterStats.totalPlayers} />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={6} lg={3}>
            <Card size="small">
              <Statistic title="Active" value={rosterStats.activePlayers} />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={6} lg={3}>
            <Card size="small">
              <Statistic title="Max Index" value={rosterStats.maxPlayerIndex} />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={6} lg={3}>
            <Card size="small">
              <Statistic title="Batters" value={rosterStats.byRole.batters} />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={6} lg={3}>
            <Card size="small">
              <Statistic title="Bowlers" value={rosterStats.byRole.bowlers} />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={6} lg={3}>
            <Card size="small">
              <Statistic title="All-Rounders" value={rosterStats.byRole.allRounders} />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={6} lg={3}>
            <Card size="small">
              <Statistic title="Wicket Keepers" value={rosterStats.byRole.wicketKeepers} />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={6} lg={3}>
            <Card size="small">
              <Statistic title="Winner Alliance" value={rosterStats.winnerAlliance} />
            </Card>
          </Col>
        </Row>
      )}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={8}>
          <form
            {...searchFormProps}
            onSubmit={(e) => {
              e.preventDefault();
              searchFormProps?.onFinish?.({
                search: (e.target as HTMLFormElement).querySelector('input')?.value,
              });
            }}
          >
            <Input.Search
              placeholder="Search by name or index..."
              allowClear
              enterButton={<SearchOutlined />}
              onSearch={(value) => searchFormProps?.onFinish?.({ search: value })}
            />
          </form>
        </Col>
        <Col
          xs={24}
          md={16}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}
        >
          <Space size={8} wrap>
            <Tooltip title="Card variant for Dropped / In Roster / In Playing XI">
              <Segmented<VariantChoice>
                size="small"
                value={variant}
                onChange={handleVariantChange}
                options={[
                  { label: 'All variants', value: ALL_VARIANTS },
                  ...CARD_VARIANTS.map((label, rarity) => ({ label, value: rarity })),
                ]}
              />
            </Tooltip>
            <Tooltip title={OWNERSHIP_HELP}>
              <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
            </Tooltip>
            <Typography.Text type="secondary">{ownershipLabel}</Typography.Text>
            {variantIgnored && (
              <Typography.Text type="warning">
                The server doesn&apos;t split counts by card variant yet, so they are hidden
              </Typography.Text>
            )}
            <Button
              size="small"
              icon={<ReloadOutlined />}
              loading={waitFor !== null}
              onClick={() => void handleRefreshCounts()}
            >
              Refresh counts
            </Button>
          </Space>
        </Col>
      </Row>
      <Table
        {...tableProps}
        rowKey="playerIndex"
        scroll={{ x: 1420 }}
        size={screens.xs ? 'small' : 'middle'}
      >
        <Table.Column dataIndex="playerIndex" title="#" width={60} sorter />
        <Table.Column dataIndex="displayName" title="Name" width={180} ellipsis sorter />
        <Table.Column
          dataIndex="teamName"
          title="Team"
          width={130}
          filterDropdown={(props) => (
            <FilterDropdown {...props}>
              <Select
                style={{ minWidth: 150 }}
                placeholder="Select team"
                allowClear
                options={TEAMS.map((t) => ({ label: t, value: t }))}
              />
            </FilterDropdown>
          )}
          render={(value: string) => value || '-'}
        />
        <Table.Column
          dataIndex="defaultPlayerType"
          title="Type"
          width={110}
          filterDropdown={(props) => (
            <FilterDropdown {...props}>
              <Select
                style={{ minWidth: 140 }}
                placeholder="Player type"
                allowClear
                options={[
                  { label: 'Batsman', value: 0 },
                  { label: 'Bowler', value: 1 },
                  { label: 'All-Rounder', value: 2 },
                ]}
              />
            </FilterDropdown>
          )}
          render={(value: number) => (
            <Tag color={PLAYER_TYPE_COLORS[value] || 'default'}>
              {PLAYER_TYPES[value] || 'Unknown'}
            </Tag>
          )}
        />
        <Table.Column
          dataIndex="defaultBowlerType"
          title="Bowler Type"
          width={120}
          responsive={['md']}
          filterDropdown={(props) => (
            <FilterDropdown {...props}>
              <Select
                style={{ minWidth: 140 }}
                placeholder="Bowler type"
                allowClear
                options={[
                  { label: 'Non-Bowler', value: -1 },
                  { label: 'Fast', value: 0 },
                  { label: 'Off Break', value: 1 },
                  { label: 'Leg Break', value: 2 },
                  { label: 'Medium', value: 3 },
                ]}
              />
            </FilterDropdown>
          )}
          render={(value: number) => (
            <Tag
              color={
                value === -1 ? 'default' : value === 0 ? 'red' : value === 3 ? 'orange' : 'green'
              }
            >
              {BOWLER_TYPES[value] || 'Unknown'}
            </Tag>
          )}
        />
        <Table.Column
          dataIndex="isWicketKeeper"
          title="WK"
          width={60}
          responsive={['md']}
          filters={[
            { text: 'Yes', value: 'true' },
            { text: 'No', value: 'false' },
          ]}
          filterMultiple={false}
          render={(value: boolean) => (value ? <Tag color="cyan">WK</Tag> : null)}
        />
        <Table.Column
          dataIndex="isWinnerAlliance"
          title="WA"
          width={60}
          filters={[
            { text: 'Yes', value: 'true' },
            { text: 'No', value: 'false' },
          ]}
          filterMultiple={false}
          render={(value: boolean) => (value ? <Tag color="orange">WA</Tag> : null)}
        />
        <Table.Column
          dataIndex="jerseyNumber"
          title="Jersey"
          width={70}
          responsive={['lg']}
          sorter
        />
        <Table.Column
          dataIndex="dropped"
          title="Dropped"
          width={100}
          sorter
          render={(_: number | undefined, record: BaseRecord) =>
            fmtCount(countOf(record, 'dropped', variant))
          }
        />
        <Table.Column
          dataIndex="inRoster"
          title="In Roster"
          width={100}
          sorter
          render={(_: number | undefined, record: BaseRecord) => {
            const value = countOf(record, 'inRoster', variant);
            const share = shareOf(value, countOf(record, 'dropped', variant));
            return share ? (
              <Tooltip title={`${share} of owners`}>{fmtCount(value)}</Tooltip>
            ) : (
              fmtCount(value)
            );
          }}
        />
        <Table.Column
          dataIndex="inPlayingXi"
          title="In Playing XI"
          width={115}
          sorter
          render={(_: number | undefined, record: BaseRecord) => {
            const value = countOf(record, 'inPlayingXi', variant);
            const share = shareOf(value, countOf(record, 'inRoster', variant));
            return share ? (
              <Tooltip title={`${share} of those with him in the squad`}>{fmtCount(value)}</Tooltip>
            ) : (
              fmtCount(value)
            );
          }}
        />
        <Table.Column
          dataIndex="isActive"
          title="Status"
          width={80}
          filterDropdown={(props) => (
            <FilterDropdown {...props}>
              <Select
                style={{ minWidth: 100 }}
                placeholder="Status"
                allowClear
                options={[
                  { label: 'Active', value: true },
                  { label: 'Inactive', value: false },
                ]}
              />
            </FilterDropdown>
          )}
          render={(value: boolean) => (
            <Tag color={value ? 'green' : 'red'}>{value ? 'Active' : 'Inactive'}</Tag>
          )}
        />
        <Table.Column
          title=""
          dataIndex="actions"
          fixed="right"
          width={120}
          render={(_, record: BaseRecord) => (
            <Space size={4}>
              <Link to={`/player-data/${record.playerIndex}/edit`}>
                <Button type="link" size="small" icon={<EditOutlined />} />
              </Link>
              <Link to={`/player-data/${record.playerIndex}`}>
                <Button type="link" size="small" icon={<EyeOutlined />} />
              </Link>
              {screens.sm && (
                <DeleteButton
                  hideText
                  size="small"
                  resource="player-data"
                  recordItemId={record.playerIndex}
                  onSuccess={() => tableQueryResult?.refetch()}
                />
              )}
            </Space>
          )}
        />
      </Table>
    </List>
  );
};
